import { z } from 'zod';
import { parseDecimal } from '@/shared/utils/currency';

export const productSchema = z.object({
  category_id: z
    .string()
    .uuid('declaration:errors.categorySelect')
    .min(1, 'declaration:errors.categoryRequired'),
  subcategory_id: z
    .string()
    .uuid('declaration:errors.subcategorySelect')
    .min(1, 'declaration:errors.subcategoryRequired'),
  full_name: z
    .string()
    .trim()
    .min(2, 'declaration:errors.fullNameRequired')
    .max(200, 'declaration:errors.tooLong'),
  quantity: z
    .number({
      required_error: 'declaration:errors.quantityMin',
      invalid_type_error: 'declaration:errors.quantityMin',
    })
    .int('declaration:errors.quantityInt')
    .min(1, 'declaration:errors.quantityMin'),
  price_per_item: z
    .number({
      required_error: 'declaration:errors.priceMin',
      invalid_type_error: 'declaration:errors.priceMin',
    })
    .min(0.01, 'declaration:errors.priceMin')
    // Same ceiling as the server (DeclarationProductWriteSerializer).
    .max(1_000_000, 'declaration:errors.priceMax'),
});

export type ProductValues = z.infer<typeof productSchema>;
export type ProductField = keyof ProductValues;
export type ProductErrors = Partial<Record<ProductField, string>>;

// Numeric fields are kept as the raw text the user typed so the input can be
// temporarily empty / partial while editing. They are parsed + validated only
// on Save (see validateDraftProduct), never clamped on change.
export type DraftProduct = Omit<ProductValues, 'quantity' | 'price_per_item'> & {
  quantity: string;
  price_per_item: string;
  client_id: string;
  server_id?: string;
};

export const emptyProduct = (): DraftProduct => ({
  client_id:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `tmp-${Math.random().toString(36).slice(2)}`,
  category_id: '',
  subcategory_id: '',
  full_name: '',
  quantity: '1',
  price_per_item: '',
});

// Strict parse for validation: empty / garbage → NaN (fails the schema).
const toNumberStrict = (raw: string): number => {
  const normalized = raw.replace(',', '.').trim();
  return normalized === '' ? Number.NaN : Number(normalized);
};

// Lenient total: empty / partial input counts as 0 without touching the field.
export const productTotal = (p: DraftProduct): number =>
  parseDecimal(p.quantity) * parseDecimal(p.price_per_item);

export const validateDraftProduct = (
  d: DraftProduct,
): { values: ProductValues | null; errors: ProductErrors } => {
  const result = productSchema.safeParse({
    category_id: d.category_id,
    subcategory_id: d.subcategory_id,
    full_name: d.full_name,
    quantity: toNumberStrict(d.quantity),
    price_per_item: toNumberStrict(d.price_per_item),
  });
  const errors: ProductErrors = {};
  if (!result.success) {
    for (const issue of result.error.issues) {
      const field = issue.path[0] as ProductField | undefined;
      if (field && !errors[field]) errors[field] = issue.message;
    }
  }
  // Prices are cents: the server rounds a 3rd decimal away, so reject it here
  // instead of silently changing the declared value.
  const priceDecimals = d.price_per_item.replace(',', '.').trim().split('.')[1] ?? '';
  if (priceDecimals.length > 2 && !errors.price_per_item) {
    errors.price_per_item = 'declaration:errors.priceDecimals';
  }
  if (result.success && Object.keys(errors).length === 0) {
    return { values: result.data, errors };
  }
  return { values: null, errors };
};
